"use client";

import { useAuth } from "@/components/AuthContext";
import { createLogger } from "@/lib/logger";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const logger = createLogger("app-data");

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface UserMemories {
  memories: Array<{
    id: string | number;
    memory: string;
    [key: string]: any;
  }>;
  lastFetched: number;
}

export interface UserLevel {
  level: number;
  title: string;
  description: string;
  icon: string;
  minMemories: number;
  maxMemories: number | null;
}

export interface QdrantContext {
  context: string;
  languageName: string;
  languageLevel: string;
  cachedAt: number;
}

export interface UserSettings {
  languageCode: string | null;
  languageName: string | null;
  languageLevel: string | null;
  teachingStyle: string | null;
  voice: string | null;
  fromLanguageCode: string | null;
  fromLanguageName: string | null;
}

interface AppDataContextType {
  // User memories from mem0
  memories: UserMemories | null;
  isLoadingMemories: boolean;
  refreshMemories: () => Promise<void>;
  getMemoriesSummary: (maxCount?: number) => string;

  // User level based on memory count
  userLevel: UserLevel | null;
  isLoadingUserLevel: boolean;
  refreshUserLevel: () => Promise<void>;

  // Qdrant context (preloaded for current language)
  qdrantContext: QdrantContext | null;
  isLoadingQdrant: boolean;
  preloadQdrantContext: (languageName: string, languageLevel: string) => Promise<void>;

  // User settings from localStorage
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => void;

  // Utility
  isInitialized: boolean;
  refreshAll: () => Promise<void>;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

// ============================================================================
// CACHE CONFIGURATION
// ============================================================================

const CACHE_DURATION = {
  MEMORIES: 20 * 60 * 1000, // 20 minutes
  USER_LEVEL: 10 * 60 * 1000, // 10 minutes
  QDRANT: 24 * 60 * 60 * 1000, // 24 hours (static content)
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getSettingsFromLocalStorage(): UserSettings {
  if (typeof window === "undefined") {
    return {
      languageCode: null,
      languageName: null,
      languageLevel: null,
      teachingStyle: null,
      voice: null,
      fromLanguageCode: null,
      fromLanguageName: null,
    };
  }

  return {
    languageCode: localStorage.getItem("languageCode"),
    languageName: localStorage.getItem("languageName"),
    languageLevel: localStorage.getItem("languageLevel"),
    teachingStyle: localStorage.getItem("teachingStyle"),
    voice: localStorage.getItem("voice"),
    fromLanguageCode: localStorage.getItem("fromLanguageCode"),
    fromLanguageName: localStorage.getItem("fromLanguageName"),
  };
}

function saveSettingsToLocalStorage(settings: UserSettings) {
  if (typeof window === "undefined") return;

  Object.entries(settings).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  });
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // State
  const [memories, setMemories] = useState<UserMemories | null>(null);
  const [isLoadingMemories, setIsLoadingMemories] = useState(false);

  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [isLoadingUserLevel, setIsLoadingUserLevel] = useState(false);

  const [qdrantContext, setQdrantContext] = useState<QdrantContext | null>(null);
  const [isLoadingQdrant, setIsLoadingQdrant] = useState(false);

  const [settings, setSettings] = useState<UserSettings>({
    languageCode: null,
    languageName: null,
    languageLevel: null,
    teachingStyle: null,
    voice: null,
    fromLanguageCode: null,
    fromLanguageName: null,
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // In-flight request deduplication - prevents concurrent duplicate requests
  const inFlightRequestsRef = useRef<{
    memories: Promise<void> | null;
    userLevel: Promise<void> | null;
  }>({ memories: null, userLevel: null });

  // ============================================================================
  // FETCH FUNCTIONS
  // ============================================================================

  const fetchMemories = useCallback(async () => {
    if (!user?.email) return;

    // Capture email to satisfy TypeScript type checking
    const userEmail = user.email;

    // Check if a request is already in-flight
    if (inFlightRequestsRef.current.memories) {
      logger.debug("Memory request already in-flight, waiting for completion");
      return inFlightRequestsRef.current.memories;
    }

    // Check cache validity (removed memories from dependencies to avoid infinite loops)
    const now = Date.now();
    if (memories && now - memories.lastFetched < CACHE_DURATION.MEMORIES) {
      logger.debug("Using cached memories", {
        ageSeconds: Math.round((now - memories.lastFetched) / 1000),
      });
      return;
    }

    setIsLoadingMemories(true);

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        const userId = userEmail.trim();
        const response = await fetch(`/api/memory?userId=${encodeURIComponent(userId)}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch memories: ${response.statusText}`);
        }

        const data = (await response.json()) as any;
        const newMemories = {
          memories: data.memories || [],
          lastFetched: now,
        };

        // Only update state if data actually changed
        setMemories((prev) => {
          if (
            prev &&
            prev.memories.length === newMemories.memories.length &&
            JSON.stringify(prev.memories) === JSON.stringify(newMemories.memories)
          ) {
            logger.debug("Memories unchanged, keeping cache timestamp");
            return { ...prev, lastFetched: now };
          }
          logger.debug("Loaded memories for user", { count: newMemories.memories.length });
          return newMemories;
        });
      } catch (error) {
        logger.error("Error fetching memories", {
          error: error instanceof Error ? error.message : String(error),
        });
        setMemories({ memories: [], lastFetched: now });
      } finally {
        setIsLoadingMemories(false);
        // Clear in-flight request
        inFlightRequestsRef.current.memories = null;
      }
    })();

    // Register this request to deduplicate concurrent requests
    inFlightRequestsRef.current.memories = fetchPromise;
    return fetchPromise;
  }, [user?.email, memories]); // Removed 'memories' from dependencies to prevent refetch loops

  const fetchUserLevel = useCallback(async () => {
    if (!user?.email) return;

    // Capture email to satisfy TypeScript type checking
    const userEmail = user.email;

    // Check if a request is already in-flight
    if (inFlightRequestsRef.current.userLevel) {
      logger.debug("User level request already in-flight, waiting for completion");
      return inFlightRequestsRef.current.userLevel;
    }

    setIsLoadingUserLevel(true);

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        const response = await fetch("/api/user-level", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userEmail }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user level: ${response.statusText}`);
        }

        const data = (await response.json()) as any;
        setUserLevel(data.userLevel);

        logger.debug("User level loaded", {
          title: data.userLevel?.title,
          memoryCount: data.memoryCount,
        });
      } catch (error) {
        logger.error("Error fetching user level", {
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setIsLoadingUserLevel(false);
        // Clear in-flight request
        inFlightRequestsRef.current.userLevel = null;
      }
    })();

    // Register this request to deduplicate concurrent requests
    inFlightRequestsRef.current.userLevel = fetchPromise;
    return fetchPromise;
  }, [user?.email]);

  const preloadQdrantContext = useCallback(
    async (languageName: string, languageLevel: string) => {
      if (!languageName || !languageLevel) return;

      // Check if we already have this context cached
      if (
        qdrantContext &&
        qdrantContext.languageName === languageName &&
        qdrantContext.languageLevel === languageLevel &&
        Date.now() - qdrantContext.cachedAt < CACHE_DURATION.QDRANT
      ) {
        logger.debug("Using cached Qdrant context");
        return;
      }

      setIsLoadingQdrant(true);
      try {
        // Note: You'll need to create this API endpoint to fetch Qdrant context
        // For now, this is a placeholder that demonstrates the pattern
        const response = await fetch("/api/qdrant-context", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ languageName, languageLevel }),
        });

        if (response.ok) {
          const data = (await response.json()) as any;
          setQdrantContext({
            context: data.context || "",
            languageName,
            languageLevel,
            cachedAt: Date.now(),
          });
          logger.debug("Preloaded Qdrant context", { languageName, languageLevel });
        }
      } catch (error) {
        logger.error("Error preloading Qdrant context", {
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setIsLoadingQdrant(false);
      }
    },
    [qdrantContext]
  );

  // ============================================================================
  // UPDATE FUNCTIONS
  // ============================================================================

  const updateSettings = useCallback((newSettings: Partial<UserSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      saveSettingsToLocalStorage(updated);
      return updated;
    });
  }, []);

  const refreshMemories = useCallback(async () => {
    // Force refresh by clearing cache
    setMemories(null);
    await fetchMemories();
  }, [fetchMemories]);

  const refreshUserLevel = useCallback(async () => {
    await fetchUserLevel();
  }, [fetchUserLevel]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshMemories(), refreshUserLevel()]);
  }, [refreshMemories, refreshUserLevel]);

  const getMemoriesSummary = useCallback(
    (maxCount: number = 10): string => {
      if (!memories || memories.memories.length === 0) {
        return "";
      }

      const memorySamples: string[] = [];
      for (const memory of memories.memories.slice(0, maxCount)) {
        const content = memory.memory || "";
        if (content) {
          // Truncate long memories to keep summary compact
          const truncated = content.length > 100 ? content.slice(0, 97) + "..." : content;
          memorySamples.push(`- ${truncated}`);
        }
      }

      if (memorySamples.length > 0) {
        return (
          "The following are summaries from the user's previous conversations:\n" +
          memorySamples.join("\n")
        );
      }
      return "";
    },
    [memories]
  );

  // ============================================================================
  // INITIALIZATION EFFECT
  // ============================================================================

  // Load settings from localStorage on client-side mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      setSettings(getSettingsFromLocalStorage());
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      if (!user) {
        setIsInitialized(true);
        return;
      }

      logger.debug("Initializing data for user", { email: user.email });

      // Fetch all user data in parallel
      await Promise.all([fetchMemories(), fetchUserLevel()]);

      // Preload Qdrant context if language settings exist
      const currentSettings = getSettingsFromLocalStorage();
      if (currentSettings.languageName && currentSettings.languageLevel) {
        await preloadQdrantContext(currentSettings.languageName, currentSettings.languageLevel);
      }

      if (mounted) {
        setIsInitialized(true);
        logger.debug("Initialization complete");
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [user, fetchMemories, fetchUserLevel, preloadQdrantContext]);

  // ============================================================================
  // AUTO-REFRESH ON SETTINGS CHANGE
  // ============================================================================

  useEffect(() => {
    // When language settings change, preload new Qdrant context
    if (settings.languageName && settings.languageLevel && isInitialized) {
      preloadQdrantContext(settings.languageName, settings.languageLevel);
    }
  }, [settings.languageName, settings.languageLevel, isInitialized, preloadQdrantContext]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: AppDataContextType = {
    memories,
    isLoadingMemories,
    refreshMemories,
    getMemoriesSummary,

    userLevel,
    isLoadingUserLevel,
    refreshUserLevel,

    qdrantContext,
    isLoadingQdrant,
    preloadQdrantContext,

    settings,
    updateSettings,

    isInitialized,
    refreshAll,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAppData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }
  return context;
}
